// IdUpload — Step 1 of the Identity Gate wizard.
//
// The candidate uploads a government-issued ID (Aadhaar, PAN, passport,
// driver's licence). Two capture paths:
//   1. File picker — phone-shot photo of the ID
//   2. Webcam capture — hold the ID up to the laptop camera
//
// Whichever path is used, the image is POSTed to /api/identity/upload-id
// where Rekognition DetectFaces validates that a face is visible. The
// face-confidence value comes back in the response and is shown to the
// candidate as immediate feedback ("✓ face detected" / "⚠ no face found").
//
// We deliberately don't BLOCK the upload if DetectFaces fails — the
// candidate might be uploading a slightly off-angle photo that still
// works for CompareFaces later. The /verify step is the final authority.

import React, { useState } from 'react';
import {
  Upload, Camera, CheckCircle2, AlertTriangle, Loader2, RefreshCw,
} from 'lucide-react';
import WebcamCapture from './WebcamCapture';

const MAX_FILE_BYTES = 10 * 1024 * 1024;  // matches backend cap

export default function IdUpload({ onUpload, busy }) {
  const [mode, setMode] = useState('choose');     // choose | file | webcam | done
  const [result, setResult] = useState(null);     // { face_found, face_confidence, ... }
  const [error, setError] = useState(null);

  const handleFilePick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';   // allow re-picking the same file
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setError(`File too large (max ${(MAX_FILE_BYTES / 1024 / 1024).toFixed(0)} MB)`);
      return;
    }
    setError(null);
    const res = await onUpload(file);
    if (res?.ok) {
      setResult(res);
      setMode('done');
    } else {
      setError(res?.error || 'Upload failed');
    }
  };

  const handleWebcamCapture = async (blob) => {
    setError(null);
    const res = await onUpload(blob);
    if (res?.ok) {
      setResult(res);
      setMode('done');
    } else {
      setError(res?.error || 'Upload failed');
    }
  };

  const retake = () => {
    setResult(null);
    setError(null);
    setMode('choose');
  };

  // ── Choose capture method ──────────────────────────────────────────────
  if (mode === 'choose') {
    return (
      <div className="space-y-4">
        <Heading>Upload your ID document</Heading>
        <p className="text-sm text-gray-600">
          Please upload a clear photo of a government-issued photo ID
          (Aadhaar, PAN, passport, or driver's licence). Your name and face
          must be clearly visible.
        </p>

        <div className="grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => setMode('file')}
            disabled={busy}
            className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <Upload className="w-8 h-8 text-gray-500" />
            <span className="text-sm font-medium">Upload from device</span>
            <span className="text-xs text-gray-500">PNG, JPG up to 10 MB</span>
          </button>

          <button
            type="button"
            onClick={() => setMode('webcam')}
            disabled={busy}
            className="flex flex-col items-center justify-center gap-2 p-6 border-2 border-dashed border-gray-300 hover:border-blue-500 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
          >
            <Camera className="w-8 h-8 text-gray-500" />
            <span className="text-sm font-medium">Use webcam</span>
            <span className="text-xs text-gray-500">Hold ID up to camera</span>
          </button>
        </div>

        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }

  // ── File picker mode ───────────────────────────────────────────────────
  if (mode === 'file') {
    return (
      <div className="space-y-4">
        <Heading>Upload your ID document</Heading>
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          onChange={handleFilePick}
          disabled={busy}
          className="block w-full text-sm text-gray-700
            file:mr-4 file:py-2 file:px-4
            file:rounded-md file:border-0
            file:text-sm file:font-medium
            file:bg-blue-600 file:text-white
            hover:file:bg-blue-700
            file:cursor-pointer"
        />
        {busy && (
          <div className="flex items-center text-sm text-gray-500">
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Uploading and analysing…
          </div>
        )}
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <BackButton onClick={() => setMode('choose')} disabled={busy} />
      </div>
    );
  }

  // ── Webcam mode ────────────────────────────────────────────────────────
  if (mode === 'webcam') {
    return (
      <div className="space-y-4">
        <Heading>Hold ID up to the camera</Heading>
        <p className="text-sm text-gray-600">
          Hold your ID document close to the camera, well-lit, with the
          photo clearly visible. Click Capture when ready.
        </p>
        <WebcamCapture
          onCapture={handleWebcamCapture}
          facingMode="user"
          mirror={false}              // important — ID text must read correctly
          width={480}
          height={300}
          ctaLabel="Capture ID"
        />
        {error && (
          <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-800">
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <span>{error}</span>
          </div>
        )}
        <BackButton onClick={() => setMode('choose')} disabled={busy} />
      </div>
    );
  }

  // ── Done — show result + retake option ─────────────────────────────────
  if (mode === 'done' && result) {
    const ok = result.face_found;
    return (
      <div className="space-y-4">
        <Heading>ID uploaded</Heading>
        <div className={`flex items-start gap-3 p-4 rounded-lg border ${
          ok ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'
        }`}>
          {ok ? (
            <CheckCircle2 className="w-5 h-5 text-green-600 shrink-0 mt-0.5" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          )}
          <div className="text-sm">
            {ok ? (
              <>
                <div className="font-medium text-green-900">
                  Face detected on document
                </div>
                <div className="text-green-700 mt-0.5">
                  Confidence: {Number(result.face_confidence || 0).toFixed(1)}%.
                  You can proceed to the selfie step.
                </div>
              </>
            ) : (
              <>
                <div className="font-medium text-amber-900">
                  No face detected on document
                </div>
                <div className="text-amber-700 mt-0.5">
                  The ID was uploaded, but no face was clearly visible.
                  You can continue, but verification may require manual review.
                  Retake for best results.
                </div>
              </>
            )}
          </div>
        </div>

        <button
          type="button"
          onClick={retake}
          disabled={busy}
          className="inline-flex items-center text-sm text-blue-700 hover:text-blue-900"
        >
          <RefreshCw className="w-4 h-4 mr-1" />
          Retake / re-upload
        </button>
      </div>
    );
  }

  return null;
}


function Heading({ children }) {
  return <h3 className="text-lg font-semibold text-gray-900">{children}</h3>;
}

function BackButton({ onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="text-sm text-gray-600 hover:text-gray-900 disabled:opacity-50"
    >
      ← Back
    </button>
  );
}
